import usePrivateAxios from "@/common/api/usePrivateAxios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { MissionSchema } from "../schemas/mission.schema";
import { format, parse } from "date-fns";
import {
  frontendRewardTypeToBackend,
  parseRequirementQuantity,
} from "../utils/missionMapper";
import {
  Mission,
  MissionReward,
  MissionRewardType,
} from "../types/mission.type";
import { MissionsTransformer } from "../services/missionsTransformer";

interface UseUpdateMissionProps {
  onSuccess?: (response: AxiosResponse) => void;
  onError?: (error: AxiosError) => void;
}

type UpdateVariables = {
  id: string;
  form: MissionSchema;
};

const isTemporaryId = (value?: string) =>
  !value || String(value).startsWith("mission-field");

const parseDateField = (value?: string) => {
  if (!value) return undefined;
  try {
    const parsed = parse(value, "dd/MM/yyyy", new Date());
    return format(parsed, "yyyy-MM-dd");
  } catch {
    return value;
  }
};

const normalizeText = (value?: string) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const OPTIONAL_QUANTITY_LABELS = ["completar missao assim que iniciada"];

const isQuantityOptional = (label?: string) => {
  const normalized = normalizeText(label);
  if (!normalized) return false;
  return OPTIONAL_QUANTITY_LABELS.some((text) => normalized.includes(text));
};

const resolveRequirementQuantity = (
  condition: MissionSchema["conditions"][number],
) => {
  const parsed = parseRequirementQuantity(condition.value_label);
  if (parsed > 0) return parsed;
  return isQuantityOptional(condition.type_label) ? 1 : parsed;
};

const getRewardKeyFromReward = (
  reward:
    | Pick<MissionReward, "product_id" | "reward_id">
    | MissionSchema["rewards"][number],
) => {
  const key = reward.product_id || reward.reward_id;
  return key ? String(key) : undefined;
};

const resolveRelationIdFromReward = (
  reward: MissionSchema["rewards"][number],
) => {
  const relationCandidates = [
    reward.mission_reward_id,
    reward.pivot_id,
    reward.id,
  ];
  const resolved = relationCandidates.find(
    (value) => value && !isTemporaryId(String(value)),
  );
  return resolved ? String(resolved) : undefined;
};

const resolveRelationIdFromSnapshotReward = (reward: MissionReward) => {
  const relationCandidates = [
    reward.mission_reward_id,
    reward.pivot_id,
    reward.id,
  ];
  const resolved = relationCandidates.find(
    (value) => value && !isTemporaryId(String(value)),
  );
  return resolved ? String(resolved) : undefined;
};

type BuildMissionPayloadParams = {
  form: MissionSchema;
  missionSnapshot?: Mission;
  relationIdByRewardKey: Map<string, string>;
};

const buildMissionUpdatePayload = ({
  form,
  missionSnapshot,
  relationIdByRewardKey,
}: BuildMissionPayloadParams) => {
  const retainedRelationIds = new Set<string>();

  const activeRewardsPayload = form.rewards.map((rew) => {
    const explicitRelationId = resolveRelationIdFromReward(rew);
    const rewardKey = getRewardKeyFromReward(rew);
    const cachedRelationId =
      !explicitRelationId && rewardKey
        ? relationIdByRewardKey.get(rewardKey)
        : undefined;
    const relationId = explicitRelationId || cachedRelationId;

    if (relationId) {
      retainedRelationIds.add(relationId);
    }

    const rewardProperties: Record<string, unknown> = {};
    if (rew.reward_label) rewardProperties.label = rew.reward_label;
    if (rew.product_name) rewardProperties.product_name = rew.product_name;
    
    const frontendRewardType = rew.reward_type as MissionRewardType;
    const isPointsReward = frontendRewardType === "points";
    const backendRewardType = frontendRewardTypeToBackend(frontendRewardType);
    
    const normalizedRewardId = !isPointsReward
      ? rew.product_id || rew.reward_id
      : undefined;

    return {
      id: relationId,
      reward_type: isPointsReward ? null : backendRewardType,
      reward_id: isPointsReward
        ? null
        : normalizedRewardId
          ? String(normalizedRewardId)
          : undefined,
      quantity: Number(rew.quantity ?? 1),
      properties: rewardProperties,
    };
  });

  const removedRewards = (missionSnapshot?.rewards || [])
    .map((reward: MissionReward) => resolveRelationIdFromSnapshotReward(reward))
    .filter((relationId): relationId is string => {
      if (!relationId) return false;
      if (retainedRelationIds.has(relationId)) return false;
      return true;
    })
    .map((relationId) => ({ id: relationId, _destroy: true }));

  const formConditionIds = new Set<string>();

  const requirementPayload = form.conditions.map((cond) => {
    const isTemp = isTemporaryId(cond.id);
    const properties = {
      ...(cond.properties || {}),
      ...(cond.type_label ? { label: cond.type_label } : {}),
      ...(cond.condition_group_label
        ? { condition_group_label: cond.condition_group_label }
        : {}),
      ...(cond.value_label ? { value_label: cond.value_label } : {}),
    };

    if (isTemp) {
      return {
        provider_klass: cond.provider_klass || cond.type_label,
        quantity: resolveRequirementQuantity(cond),
        condition_group_label: cond.condition_group_label || "general",
        properties,
      };
    }

    formConditionIds.add(String(cond.id));
    return {
      id: String(cond.id),
      quantity: resolveRequirementQuantity(cond),
      properties,
    };
  });

  const removedRequirements = (missionSnapshot?.conditions || [])
    .filter(
      (condition: Mission["conditions"][number]) =>
        condition.id &&
        !isTemporaryId(String(condition.id)) &&
        !formConditionIds.has(String(condition.id)),
    )
    .map((condition) => ({ id: String(condition.id), _destroy: true }));

  const maxPerUser = form.allow_multiple_times ? form.max_times : 1;
  const restrictEstablishments =
    form.mission_type === "establishment"
      ? form.restrict_establishments
      : false;

  return {
    mission_type: form.mission_type,
    display_name: form.name,
    description: form.description,
    status: form.status,
    minimum_loyalty: form.minimum_loyalty,
    available_from: parseDateField(form.start_date),
    available_until: parseDateField(form.end_date),
    maximum_completions_per_user: maxPerUser || null,
    establishment_company_branch_id:
      form.mission_type === "establishment" ? form.establishment?.id : null,
    restrict_establishments: restrictEstablishments,
    allowed_establishment_company_branch_ids: restrictEstablishments
      ? form.allowed_establishments.map((item) => item.id)
      : [],
    requirements_attributes: [...requirementPayload, ...removedRequirements],
    rewards_attributes: [...activeRewardsPayload, ...removedRewards],
  };
};

export const useUpdateMission = ({
  onSuccess,
  onError,
}: UseUpdateMissionProps = {}) => {
  const privateAxios = usePrivateAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateMission"],
    mutationFn: async ({ id, form }: UpdateVariables) => {
      let missionSnapshot = queryClient.getQueryData(["mission", id]) as
        | Mission
        | undefined;

      const relationIdByRewardKey = new Map<string, string>();
      const hydrateRelationMap = (mission?: Mission) => {
        relationIdByRewardKey.clear();
        mission?.rewards?.forEach((reward) => {
          const relationId = resolveRelationIdFromSnapshotReward(reward);
          const rewardKey = getRewardKeyFromReward(reward);
          if (!relationId || !rewardKey) return;
          relationIdByRewardKey.set(rewardKey, relationId);
        });
      };
      hydrateRelationMap(missionSnapshot);

      const needsSnapshotRefresh = form.rewards.some((reward) => {
        const rewardKey = getRewardKeyFromReward(reward);
        if (!rewardKey) return false;
        const explicitRelationId = resolveRelationIdFromReward(reward);
        if (explicitRelationId) return false;
        return !relationIdByRewardKey.has(rewardKey);
      });

      if (needsSnapshotRefresh) {
        try {
          const response = await privateAxios.get(`/tmais/missions/${id}`, {
            params: {
              include: [
                "mission_rewards",
                "mission_rewards.rewardable",
                "rewards",
                "rewards.rewardable",
              ].join(","),
            },
          });
          const rawData = response.data?.data || response.data || {};
          const included = response.data?.included;
          const latestMission = MissionsTransformer.fromApi(rawData, included);
          missionSnapshot = latestMission;
          queryClient.setQueryData(["mission", id], latestMission);
          hydrateRelationMap(latestMission);
        } catch (error) {
          console.error(
            "[MissionUpdate] Failed to hydrate rewards before update",
            error,
          );
        }
      }

      const payload = buildMissionUpdatePayload({
        form,
        missionSnapshot,
        relationIdByRewardKey,
      });

      return privateAxios.put(`/tmais/missions/${id}`, { mission: payload });
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ["mission", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["missions"] });
      onSuccess?.(response);
    },
    onError,
  });
};